<script setup>
import { defineProps, reactive } from 'vue';
import axios from 'axios';
import { ref, onMounted } from 'vue';
import Paginator from '@/components/datatables/Paginator.vue';
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { formatDateToWeb } from '../../helpers/date-functions';
import { formatCurrency } from '../../helpers/function';
import { usePermissions } from '@/composables/usePermissions'; // Added import

const props = defineProps({
    programList: Array,
})

// Initialize permissions - Added here
const permissions = usePermissions();

const rangeDateconfig = {
    mode: "range",
    dateFormat: "Y-m-d",
}

// state
const result = ref([]);
const loading = ref(false);
const message = ref('');
const filters = reactive({
    search: null,
    date: null,
    program_id: "",
});
const errorMessage = ref('');

// methods
const fetchData = async (page = 1) => {        
    loading.value = true;
    try {
        const params = { 
            page: page, 
            key_search: filters.search,
            date: filters.date,
            program_id: filters.program_id,
        };

        const response = await axios.post(`${process.env.VUE_APP_API_BASEURL}filters/contracts`,params);
        if (response.status != 200) {
            alert(response.data.message);
        }
        result.value = response.data;
    } catch (error) {
        console.error(error);
        errorMessage.value = 'Failed to load the data! Please try again later.';
    } finally {
        loading.value = false;
    }
};

const resetForm = () => {
    filters.key_search = null;
    filters.date = null;
    filters.program_id = ""
    errorMessage.value = '';

    fetchData();
};

const onSearchHandler = (e) => {
    e.preventDefault();    
    fetchData();
};

const downloadExcel = ()=>{
    // Check if user has export permission - Added permission check
    if (!permissions.canExport?.value) {
        alert('You do not have permission to export data.');
        return;
    }

    const params = {
        key_search: filters.search,
        date: filters.date,
        program_id: filters.program_id,
    };
    if(params.date == null){
        alert('Please select date');
        return;
    }

    loading.value = true;
    message.value = 'Data being exported, please wait...';

    axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt')
    axios
        .post(process.env.VUE_APP_API_BASEURL+'contract/export', params, {responseType: 'blob'})
        .then(response => {
            if (response) {
                var fileURL = window.URL.createObjectURL(new Blob([response.data]));
                var fileLink = document.createElement('a');

                fileLink.href = fileURL;
                fileLink.setAttribute('download', 'Customers List.xlsx');
                document.body.appendChild(fileLink);

                fileLink.click();
            }
        })
        .catch(error => {
            console.log(error);
            alert('Error downloading file');
        })
        .finally(()=> {
            loading.value = false;
            message.value = '';
        });
};

// lifecycle
onMounted(() => {
    axios.defaults.headers.common['authorization'] = 'Bearer ' + localStorage.getItem('jwt');
    console.log('onMounted');
    fetchData();
});

</script>

<template>
    <div>
        <!-- searching -->
        
        <div class="card-body border-bottom-dashed border-bottom">
            <form @submit="onSearchHandler">
                <div class="row g-3">
                    <div class="col-xl-5">
                        <div class="search-box">
                            <input type="text" class="form-control search"
                            placeholder="Search for Customer Name, Order Number, Contract Number" v-model="filters.search"/>
                            <i class="ri-search-line search-icon"></i>
                        </div>
                    </div>
                    <div class="col-xl-7">
                        <div class="row g-3">
                            <div class="col-sm-6">
                                <div class="">
                                    <flat-pickr v-model="filters.date" placeholder="Select Contract Signed Date" :config="rangeDateconfig"
                                    class="form-control"></flat-pickr>
                                </div>
                            </div>                  
                        
                            <div class="col-sm-6">
                                <select v-model="filters.program_id" class="form-select mb-4">
                                    <option value="" disabled>Select Program</option>
                                    <option v-for="(item, index) in props?.programList" :key="index" :value="item.id" >
                                        {{ item.program_name }}
                                    </option>
                                </select>
                            </div>                  
                        </div>
                    </div>
                </div>

                <div class="row g-3 justify-content-end">
                    <div class="col-xl-2">
                        <div>
                            <button type="submit" class="btn btn-primary w-100" :disabled="loading">
                                <i class="ri-equalizer-fill me-2 align-bottom"></i>Search
                            </button>
                        </div>
                    </div>
                    <div class="col-xl-2">
                        <div>
                            <button type="button" @click='resetForm' class="btn btn-danger w-100"  :disabled="loading">
                                <i class="ri-refresh-fill me-2 align-bottom"></i>Reset
                            </button>
                        </div>
                    </div>
                    <div class="col-xl-2">
                        <div>
                            <!-- Added permission check for export button -->
                            <button type="button" 
                                    @click="downloadExcel()" 
                                    class="btn btn-success w-100"  
                                    :disabled="loading || !permissions.canExport?.value"
                                    :title="!permissions.canExport?.value ? 'No export permission' : 'Export to Excel'">
                                <i class="ri-file-excel-2-line me-2 align-bottom"></i>Export
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
        <div>
            {{ message }}
        </div>
        <div class="table-responsive mt-3">
            <table class="table table-striped table-nowrap mb-0">
                <thead class="table-secondary">
                    <tr>
                        <th>#.</th>
                        <th scope="col">Contract Number</th>
                        <th scope="col">Customer Name</th>
                        <th scope="col">SKU</th>
                        <th scope="col">IMEI</th>
                        <th scope="col">Order Number</th>
                        <th scope="col">Program</th>
                        <th scope="col">Device Name</th>
                        <th scope="col">Monthly Subscription</th>
                        <th scope="col">Contract Signed Date</th>
                    </tr>
                </thead>
                <tbody>                        
                    <tr v-for="(item, index) in result.data" :key="item.id">
                        <td>{{ index + result.from }}</td>
                        <td><router-link :to="'/contracts/customer-contract-details/'+item.contract_id" class="fw-medium link-secondary">{{ item.contract_number }}</router-link></td>                               
                        <td>{{ item.name_as_per_nric }}</td>
                        <td>{{ item.sku }}</td>
                        <td>{{ item.imei }}</td>
                        <td><router-link :to="'/orders/customer-order-details/'+item.cid" class="fw-medium link-secondary">{{ item.orderId }}</router-link></td>
                        <td>{{ item.program_name }}</td>
                        <td>{{ item.product_name }}</td>
                        <td style="text-align: right;">{{ formatCurrency(item.price) }}</td>
                        <td style="text-align: center;">{{ formatDateToWeb(item.created_at, 'DD-MM-YYYY, H:mm') }}</td>
                    </tr>                    
                    <tr v-if="errorMessage">
                        <td colspan="16" style="text-align: center;">{{ errorMessage }}</td>
                    </tr>
                    <tr v-if="result.data != undefined && result.data.length == 0">
                        <td colspan="16" style="text-align: center;">No data</td>
                    </tr>
                </tbody>
            </table>
            <div class="mt-4" style="display: flex; justify-content: space-between; align-items: center;">
                <div>Showing {{ result.from }} to {{ result.to }} of {{ result.total }} results</div>
                <Paginator :data="result" :limit="1" @pagination-change-page="fetchData"></Paginator>
            </div>
        </div>
    </div>
</template>